<!-- ADORN Carousel with a configurable number of items per slide & autoscroll capabilities -->
<template>
  <section
    v-bind="$attrs"
    ref="carouselRef"
    class="carousel-wrapper py-xl w-100"
    :style="{ height: carouselHeight }"
    role="region"
    aria-roledescription="carousel"
    aria-label="Auto-scrolling carousel"
    @mouseenter="pause"
    @mouseleave="() => {
      if (shouldAutoScroll && !autoscrollOnce) {
        resume()
      }
    }">
    <Transition
      :name="direction === 'next' ? 'slide-next' : 'slide-prev'">
      <div
        :key="currentIndex"
        v-drag="dragHandler()"
        class="carousel-slide row h-100 mx-auto"
        :drag-options="dragOptions"
        :style="{ height: carouselHeight }"
        :class="[
          direction === 'next' ? 'next' : '',
          direction === 'previous' ? 'previous' : ''
        ]">
        <div
          v-for="(item, index) in currentChunk"
          ref="slideItem"
          :key="item.id || index"
          :class="columnClass"
          role="group"
          :aria-roledescription="'slide'"
          :aria-label="`Slide ${currentIndex + 1} of ${chunkedItems.length}`"
          aria-live="polite"
          :aria-hidden="currentIndex !== index">
          <slot
            :item="item"
            :index="index"/>
        </div>
      </div>
    </Transition>
    <Teleport
      v-if="showButtons"
      :to="buttonContainer"
      :disabled="!teleportScrollButtons">
      <div
        class="absolute-buttons"
        :class="{'custom' : teleportScrollButtons}">
        <ScrollButton
          direction="left"
          :class="{'visually-hidden-focusable': !showButtons}"
          @click="prev"/>
        <ScrollButton
          direction="right"
          :class="{'visually-hidden-focusable': !showButtons}"
          @click="next"/>
      </div>
    </Teleport>
  </section>
  <Teleport
    v-if="!disableIndicators"
    :to="indicatorContainer"
    :disabled="!teleportIndicators">
    <div
      class="carousel-indicator-container"
      role="navigation">
      <button
        v-for="(chunk, chunkIndex) in chunkedItems"
        :key="chunkIndex"
        type="button"
        :disabled="!allowIndicatorClick"
        :class="{ active: currentIndex === chunkIndex }"
        role="tab"
        :aria-label="`Go to slide ${chunkIndex + 1}`"
        :aria-selected="currentIndex === chunkIndex ? true : null"
        @click="goTo(chunkIndex)"/>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useIntervalFn, useDebounceFn, useEventListener, useDocumentVisibility} from '@vueuse/core'
import useScreenSize from '@shared/composables/screenSize.js'
import ScrollButton from '@shared/components/ADORN/ScrollButton.vue'
import { dragDirective } from '@vueuse/gesture'
import { isTest } from '@shared/utils/environment.js'

const props = defineProps({
  items: {
    type: Array,
    required: true
  },
  itemsPerSlide: {
    type: [Object, Number],
    default: () => ({
      mobile: 1,
      tablet: 2,
      desktop: 3
    }),
    validator (value) {
      if (typeof value === 'object') {
        const requiredProps = ['mobile', 'tablet', 'desktop']
        return requiredProps.every(prop => prop in value)
      }
      return true
    }
  },
  showButtons: {
    type: Boolean,
    default: false
  },
  showIndicators: {
    type: Boolean,
    default: true
  },
  buttonContainer: {
    type: [String, HTMLElement],
    default: () => null
  },
  indicatorContainer: {
    type: [String, HTMLElement],
    default: () => null
  },
  autoScrollBehavior: {
    type: String,
    default: 'disabled',
    validator: val => ['once', 'continuous', 'drag', 'disabled'].includes(val)
  },
  allowDrag: {
    type: Boolean,
    default: true
  },
  allowIndicatorClick: {
    type: Boolean,
    default: true
  },
  wrap: {
    type: Boolean,
    default: true
  },
  startIndex: {
    type: Number,
    default: 0
  }
})

const { screenSizeValue } = useScreenSize()

const shouldAutoScroll = computed(() => props.autoScrollBehavior !== 'disabled')
const scrollOnce = computed(() => props.autoScrollBehavior === 'once')

const dragOptions = {
  filterTaps: true,
  swipeDistance: [1, 1],
  preventWindowScrollY: true,
  useTouch: true
}
const vDrag = {
  // Disable drag in unit tests, it's not needed and it causes issues with the tests
  beforeMount: isTest ? undefined : dragDirective().bind
}

const direction = ref('next')

const dragHandler = () => ({ movement: [x], dragging }) => {
  if (!props.allowDrag) {
    return
  }
  if (dragging) {
    pause()
  } else {
    if (x > 50) {
      prev()
    } else if (x < -50) {
      next()
    }
  }
}

const itemsPerSlidePerScreenSize = computed(() => {
  return typeof props.itemsPerSlide === 'number'
    ? {
        mobile: props.itemsPerSlide,
        tablet: props.itemsPerSlide,
        desktop: props.itemsPerSlide
      }
    : props.itemsPerSlide
})
const chunkedItems = computed(() => {
  const perSlide = screenSizeValue(itemsPerSlidePerScreenSize.value).value
  const chunks = []
  for (let i = 0; i < props.items.length; i += perSlide) {
    chunks.push(props.items.slice(i, i + perSlide))
  }
  return chunks
})

const currentIndex = ref(Math.min(props.startIndex, chunkedItems.value.length - 1))

watch(chunkedItems, (chunks) => {
  if (currentIndex.value >= chunks.length) {
    currentIndex.value = 0
  }
})

const currentChunk = computed(() => chunkedItems.value[currentIndex.value] || [])

const next = () => {
  direction.value = 'next'
  currentIndex.value = props.wrap
    ? (currentIndex.value + 1) % chunkedItems.value.length
    : Math.min(currentIndex.value + 1, chunkedItems.value.length - 1)
}
const prev = () => {
  direction.value = 'prev'
  currentIndex.value = props.wrap
    ? (currentIndex.value - 1 + chunkedItems.value.length) % chunkedItems.value.length
    : Math.max(currentIndex.value - 1, 0)
}
const goTo = (index) => {
  if (index > currentIndex.value) {
    direction.value = 'next'
  } else if (index < currentIndex.value) {
    direction.value = 'prev'
  }
  currentIndex.value = index
}

const disableIndicators = computed(() => { return !props.showIndicators || (chunkedItems.value.length <= 1)})
const teleportScrollButtons = computed(() => props.buttonContainer !== null)
const teleportIndicators = computed(() => props.indicatorContainer !== null)

const interval = ref(5000)
const visibility = useDocumentVisibility()
const immediate = shouldAutoScroll.value && visibility.value === 'visible'
const { pause, resume } = useIntervalFn(next, interval, { immediate })

watch(visibility, (current, previous) => {
  if (!shouldAutoScroll.value) return
  if (current === 'visible' && previous === 'hidden') {
    resume()
  } else if (current === 'hidden' && previous === 'visible') {
    pause()
  }
})

watch(currentIndex, (value) => {
  if (scrollOnce.value && value === chunkedItems.value.length - 1) {
    pause()
  }
}, { immediate: true })

// bootstrap-style columns
const columnClass = computed(() => {
  const perSlide = screenSizeValue(itemsPerSlidePerScreenSize.value).value
  return `col-${Math.floor(12 / perSlide)}`
})

const slideItem = ref(null)
const carouselHeight = ref('')
const carouselRef = ref(null)

const recalculateCarouselHeight = () => {
  nextTick(() => {
    carouselHeight.value = 'auto'
    requestAnimationFrame(() => {
      const firstSlide = slideItem.value ? slideItem.value[0] : null
      if (firstSlide) {
        const height = firstSlide.offsetHeight
        carouselHeight.value = `${height}px`
      }
    })
  })
}

onMounted(() => {
  recalculateCarouselHeight()
})

const debouncedFn = useDebounceFn(() => { recalculateCarouselHeight() }, 50, { maxWait: 5000 })

useEventListener(window, 'resize', debouncedFn)

defineExpose({
  currentIndex,
  next,
  prev,
})
</script>


<style lang="scss" scoped>
section {
  position: relative;
  overflow: hidden; 
}
.carousel-slide {
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 1; 
}
.carousel-indicator-container {
  display: flex;
  justify-content: center;
  position: static;
  margin: 0;
  column-gap: $spacing-md;
  --indicator-size: 12px;
  & > button {
    // remove all styling on safari iOS - otherwise
    // it will stretch the button to fill the container
    all: unset;
    width: var(--indicator-size);
    height: var(--indicator-size);
    border-radius: 50%;
    border: 0.5px solid $pewter;
    margin: 0;

    &.active {
      background-color: $pewter;
    }
  }
}

.scroll-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
}
.custom > .scroll-button {
  position: unset;
  top: unset;
  transform: none;
}

.scroll-button-left {
  left: 0;
}
.scroll-button-right {
  right: 0;
}
// animations
.slide-next-enter-active,
.slide-prev-enter-active,
.slide-next-leave-active,
.slide-prev-leave-active {
  transition:
    left 1.2s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  width: 100%;
  top: 0;
}
.slide-next-enter-from,
.slide-prev-enter-from {
  opacity: 0;
  z-index: 3;
}
.slide-next-enter-to,
.slide-prev-enter-to {
  opacity: 1;
  z-index: 3;
}
.slide-next-enter-from {
  left: 100%;
}
.slide-prev-enter-from {
  left: -100%;
}
.slide-next-enter-to,
.slide-prev-enter-to {
  left: 0;
}
.slide-next-leave-from,
.slide-prev-leave-from {
  opacity: 1;
  z-index: 1;
  left: 0;
}
.slide-next-leave-to {
  opacity: 0;
  left: -100%;
}
.slide-prev-leave-to {
  opacity: 0;
  left: 100%;
}

</style>
